import { Fragment, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, DownloadSimple } from 'shared/icons';
import Button from './Button';
import Input from './Input';
import Pagination from './Pagination';
import Select from './Select';
import TableShell from './TableShell';

function toSearchableString(value) {
    if (value === null || value === undefined) {
        return '';
    }

    if (Array.isArray(value)) {
        return value.map((item) => toSearchableString(item)).join(' ');
    }

    if (typeof value === 'object') {
        return Object.values(value)
            .map((item) => toSearchableString(item))
            .join(' ');
    }

    return String(value);
}

function normalizeSortValue(value) {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'number') {
        return value;
    }

    const parsedNumber = Number(value);
    if (!Number.isNaN(parsedNumber) && String(value).trim() !== '') {
        return parsedNumber;
    }

    return String(value).toLowerCase();
}

function createCsv(rows, columns) {
    const escapeCsvValue = (value) => {
        const text = toSearchableString(value).replace(/"/g, '""');
        return `"${text}"`;
    };

    const header = columns.map((column) => escapeCsvValue(column.label)).join(',');
    const lines = rows.map((row) =>
        columns
            .map((column) => {
                const rawValue = typeof column.getValue === 'function' ? column.getValue(row) : row?.[column.key];
                return escapeCsvValue(rawValue);
            })
            .join(','),
    );

    return [header, ...lines].join('\n');
}

/**
 * Tabela padrão do app: busca, sort, export CSV e paginação (client ou server)
 * com thead sticky dentro de um wrapper rolável `max-h-[70vh]`.
 *
 * DECISÃO — SEM virtualização (review agents 04-frontend-ux, item [MÉDIO]
 * "Sem virtualização em listas grandes", fechado em 2026-07): todas as
 * consumidoras vivas paginam — client por default (15/página, opções até
 * 100/200 opt-in: Users, Organizations, Campaigns, TrucksPage, PromotionsList,
 * PromotionsByItem) ou server (Orders, Customers, Payments, Purchasing,
 * UserProfilePage, wms/DashboardPage) — ou usam `variant="minimal"` em
 * datasets intrinsecamente pequenos (itens/etapas de UM outbound, romaneio de
 * UM PDF). O DOM fica em ≤100–200 <tr> no pior caso, então react-window só
 * adicionaria complexidade (altura variável de linha por causa da coluna de
 * produtos, interação com thead sticky, export/sort sobre o conjunto) sem
 * ganho mensurável. Reavaliar apenas se surgir consumidora renderizando
 * centenas de linhas SEM paginar.
 */
const StandardTable = ({
    rows = [],
    columns = [],
    rowKey = (row, index) => row?.id || index,
    emptyMessage = 'Nenhum registro encontrado.',
    searchPlaceholder = 'Pesquisar em todos os campos...',
    defaultPageSize = 15,
    pageSizeOptions = [15, 30, 50, 100],
    searchEnabled = true,
    searchMode = 'client',
    exportEnabled = true,
    exportFileName = 'dados',
    countLabel = 'registros',
    paginationEnabled = true,
    paginationMode = 'client',
    totalItems = null,
    currentPage = undefined,
    onPageChange = undefined,
    itemsPerPage = undefined,
    onItemsPerPageChange = undefined,
    searchTerm = undefined,
    onSearchTermChange = undefined,
    // Enter no campo de busca. Quem busca no SERVIDOR costuma debouncar o termo
    // antes de chamar a API; sem isto o Enter não faz nada e a pessoa fica
    // esperando o debounce. Recebe o termo já digitado — cabe à página aplicar.
    onSearchSubmit = undefined,
    sortState = undefined,
    onSortStateChange = undefined,
    sortMode = 'client',
    onExport = undefined,
    exportRows = undefined,
    exportColumns = undefined,
    toolbarLeft = null,
    toolbarRight = null,
    onRowClick = null,
    // Detalhe INLINE: `(row, index) => ReactNode | null`. Devolvendo conteúdo,
    // a tabela emite um <tr> extra logo depois da linha, com um único <td
    // colSpan> de largura total — é o lugar natural do detalhe mestre/detalhe
    // (o painel solto abaixo da tabela perde a âncora visual da linha).
    // Ausente (default) = nenhum <tr> extra e NENHUM wrapper na árvore: toda
    // consumidora atual renderiza exatamente o que renderizava antes.
    renderExpandedRow = null,
    rowClassName = '',
    tableClassName = 'w-full',
    // max-h + overflow-auto: o wrapper já era o scroll container do sticky
    // (overflow-x-auto força overflow-y auto); sem altura limitada o scroll
    // vertical acontece na página e o thead sticky nunca engata. Com o teto,
    // tabelas longas rolam aqui dentro com cabeçalho fixo (e o footer de
    // paginação permanece visível); tabelas curtas ficam como antes.
    tableWrapClassName = 'overflow-auto max-h-[70vh]',
    showRange = true,
    variant = 'default',
}) => {
    const isMinimal = variant === 'minimal';
    const resolvedSearchEnabled = isMinimal ? false : searchEnabled;
    const resolvedExportEnabled = isMinimal ? false : exportEnabled;
    const resolvedPaginationEnabled = isMinimal ? false : paginationEnabled;
    const resolvedShowRange = isMinimal ? false : showRange;

    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const [internalSortState, setInternalSortState] = useState({ columnKey: null, direction: 'none' });
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const [internalItemsPerPage, setInternalItemsPerPage] = useState(defaultPageSize);

    const effectiveSearchTerm = searchTerm ?? internalSearchTerm;
    const effectiveSortState = sortState ?? internalSortState;
    const effectiveItemsPerPage = Math.max(1, Number(itemsPerPage ?? internalItemsPerPage ?? defaultPageSize));
    const effectiveCurrentPage = Math.max(1, Number(currentPage ?? internalCurrentPage ?? 1));

    const setSearch = (value) => {
        if (typeof onSearchTermChange === 'function') {
            onSearchTermChange(value);
        }
        if (searchTerm === undefined) {
            setInternalSearchTerm(value);
        }
        if (resolvedPaginationEnabled) {
            if (typeof onPageChange === 'function') {
                onPageChange(1);
            }
            if (currentPage === undefined) {
                setInternalCurrentPage(1);
            }
        }
    };

    const setSort = (nextSortState) => {
        if (typeof onSortStateChange === 'function') {
            onSortStateChange(nextSortState);
        }
        if (sortState === undefined) {
            setInternalSortState(nextSortState);
        }
        if (resolvedPaginationEnabled) {
            if (typeof onPageChange === 'function') {
                onPageChange(1);
            }
            if (currentPage === undefined) {
                setInternalCurrentPage(1);
            }
        }
    };

    const setPage = (nextPage) => {
        if (typeof onPageChange === 'function') {
            onPageChange(nextPage);
        }
        if (currentPage === undefined) {
            setInternalCurrentPage(nextPage);
        }
    };

    const setPageSize = (nextItemsPerPage) => {
        if (typeof onItemsPerPageChange === 'function') {
            onItemsPerPageChange(nextItemsPerPage);
        }
        if (itemsPerPage === undefined) {
            setInternalItemsPerPage(nextItemsPerPage);
        }

        if (resolvedPaginationEnabled) {
            if (typeof onPageChange === 'function') {
                onPageChange(1);
            }
            if (currentPage === undefined) {
                setInternalCurrentPage(1);
            }
        }
    };

    const searchableColumns = useMemo(() => columns.filter((column) => column.searchable !== false), [columns]);

    const filteredRows = useMemo(() => {
        if (!resolvedSearchEnabled || searchMode === 'server') {
            return rows;
        }

        const term = effectiveSearchTerm.trim().toLowerCase();
        if (!term) {
            return rows;
        }

        return rows.filter((row) =>
            searchableColumns.some((column) => {
                const rawValue = typeof column.getValue === 'function' ? column.getValue(row) : row?.[column.key];
                return toSearchableString(rawValue).toLowerCase().includes(term);
            }),
        );
    }, [rows, searchableColumns, effectiveSearchTerm, resolvedSearchEnabled, searchMode]);

    const sortedRows = useMemo(() => {
        if (sortMode === 'server') {
            return filteredRows;
        }

        if (effectiveSortState.direction === 'none' || !effectiveSortState.columnKey) {
            return filteredRows;
        }

        const column = columns.find((item) => item.key === effectiveSortState.columnKey);
        if (!column) {
            return filteredRows;
        }

        const factor = effectiveSortState.direction === 'asc' ? 1 : -1;

        return [...filteredRows].sort((a, b) => {
            const valueA = normalizeSortValue(
                typeof column.getValue === 'function' ? column.getValue(a) : a?.[column.key],
            );
            const valueB = normalizeSortValue(
                typeof column.getValue === 'function' ? column.getValue(b) : b?.[column.key],
            );

            if (valueA > valueB) {
                return 1 * factor;
            }

            if (valueA < valueB) {
                return -1 * factor;
            }

            return 0;
        });
    }, [columns, filteredRows, effectiveSortState, sortMode]);

    const localTotalRows = sortedRows.length;
    const totalRows =
        paginationMode === 'server' ? Math.max(0, Number(totalItems ?? rows.length)) : Math.max(0, localTotalRows);
    const totalPages =
        resolvedPaginationEnabled && effectiveItemsPerPage > 0
            ? Math.max(1, Math.ceil(totalRows / effectiveItemsPerPage))
            : 1;
    const safePage = Math.min(effectiveCurrentPage, totalPages);
    const startIndex = (safePage - 1) * effectiveItemsPerPage;
    const displayRows =
        resolvedPaginationEnabled && paginationMode === 'client'
            ? sortedRows.slice(startIndex, startIndex + effectiveItemsPerPage)
            : sortedRows;
    const visibleCount = displayRows.length;
    const rangeStart = totalRows === 0 ? 0 : startIndex + 1;
    const rangeEnd = totalRows === 0 ? 0 : Math.min(startIndex + visibleCount, totalRows);
    const colSpan = Math.max(1, columns.length);

    useEffect(() => {
        if (!resolvedPaginationEnabled) {
            return;
        }
        if (safePage !== effectiveCurrentPage) {
            setPage(safePage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safePage, effectiveCurrentPage, resolvedPaginationEnabled]);

    const toggleSort = (column) => {
        if (column.sortable === false) {
            return;
        }

        const previous = effectiveSortState;
        let nextSortState = null;
        if (previous.columnKey !== column.key) {
            nextSortState = { columnKey: column.key, direction: 'asc' };
        } else if (previous.direction === 'asc') {
            nextSortState = { columnKey: column.key, direction: 'desc' };
        } else if (previous.direction === 'desc') {
            nextSortState = { columnKey: null, direction: 'none' };
        } else {
            nextSortState = { columnKey: column.key, direction: 'asc' };
        }
        setSort(nextSortState);
    };

    const handleExport = async () => {
        if (typeof onExport === 'function') {
            await onExport({
                rows: displayRows,
                filteredRows: sortedRows,
                allRows: rows,
            });
            return;
        }

        const finalRows = Array.isArray(exportRows)
            ? exportRows
            : paginationMode === 'server'
              ? displayRows
              : sortedRows;
        const finalColumns = Array.isArray(exportColumns)
            ? exportColumns
            : columns.filter((column) => column.exportable !== false);

        const csv = createCsv(finalRows, finalColumns);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${exportFileName}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    return (
        <TableShell
            tableWrapClassName={tableWrapClassName}
            toolbar={
                isMinimal ? null : (
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div className="flex flex-wrap items-center gap-3">
                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                <span className="font-semibold text-black dark:text-white">{totalRows}</span>{' '}
                                {countLabel} encontrados
                                {resolvedShowRange ? (
                                    <span className="ml-2">
                                        (exibindo {rangeStart}-{rangeEnd} de {totalRows})
                                    </span>
                                ) : null}
                            </p>
                            {toolbarLeft}
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            {resolvedSearchEnabled ? (
                                <div className="w-full sm:w-auto sm:min-w-[280px]">
                                    <Input
                                        value={effectiveSearchTerm}
                                        onChange={(event) => setSearch(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' && typeof onSearchSubmit === 'function') {
                                                onSearchSubmit(effectiveSearchTerm);
                                            }
                                        }}
                                        placeholder={searchPlaceholder}
                                    />
                                </div>
                            ) : null}
                            {resolvedExportEnabled ? (
                                <Button variant="secondary" onClick={handleExport}>
                                    <DownloadSimple size={18} className="mr-2" />
                                    Exportar
                                </Button>
                            ) : null}
                            {toolbarRight}
                        </div>
                    </div>
                )
            }
            footer={
                resolvedPaginationEnabled ? (
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div className="inline-flex items-center gap-2 whitespace-nowrap">
                            <span className="whitespace-nowrap text-sm text-mono-600 dark:text-mono-400">Mostrar</span>
                            <Select
                                value={effectiveItemsPerPage}
                                onChange={(event) => setPageSize(Number(event.target.value))}
                                className="w-24 px-2 py-1 text-sm"
                            >
                                {pageSizeOptions.map((optionValue) => (
                                    <option key={optionValue} value={optionValue}>
                                        {optionValue}
                                    </option>
                                ))}
                            </Select>
                            <span className="whitespace-nowrap text-sm text-mono-600 dark:text-mono-400">
                                de {totalRows}
                            </span>
                        </div>
                        {/* Implementação única de paginação do DS (ui/Pagination);
                            o range já aparece na toolbar, então sem resumo aqui. */}
                        <Pagination
                            currentPage={safePage}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            itemsPerPage={effectiveItemsPerPage}
                            totalItems={totalRows}
                            showSummary={false}
                            className="flex flex-wrap items-center gap-2"
                        />
                    </div>
                ) : null
            }
        >
            <table className={tableClassName}>
                {/* Cabeçalho fixo: sticky dentro do wrapper rolável (ver tableWrapClassName).
                    bg opaco nos dois temas (mono-50/mono-950 são rgb sem alpha) pro conteúdo
                    não vazar por trás; z-10 acima das células; a linha de baixo é inset
                    shadow porque border em thead sticky não acompanha o scroll
                    (border-collapse do preflight pinta a borda na tabela, não no thead). */}
                <thead className="sticky top-0 z-10 bg-mono-50 dark:bg-mono-950 shadow-[inset_0_-1px_0_0_rgb(var(--mono-200))] dark:shadow-[inset_0_-1px_0_0_rgb(var(--mono-800))]">
                    <tr>
                        {columns.map((column) => {
                            const align = column.align || 'left';
                            const alignHeaderClass =
                                align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
                            const justifyClass =
                                align === 'right'
                                    ? 'justify-end'
                                    : align === 'center'
                                      ? 'justify-center'
                                      : 'justify-start';

                            return (
                                <th
                                    key={column.key}
                                    className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-mono-600 dark:text-mono-400 ${alignHeaderClass} ${column.headerClassName || ''}`.trim()}
                                >
                                    <button
                                        type="button"
                                        className={`inline-flex w-full items-center gap-1 ${justifyClass} ${
                                            column.sortable === false
                                                ? 'cursor-default'
                                                : 'cursor-pointer hover:text-black dark:hover:text-white'
                                        }`}
                                        onClick={() => toggleSort(column)}
                                    >
                                        <span>{column.label}</span>
                                        {effectiveSortState.columnKey === column.key &&
                                        effectiveSortState.direction === 'asc' ? (
                                            <ArrowUp size={12} />
                                        ) : null}
                                        {effectiveSortState.columnKey === column.key &&
                                        effectiveSortState.direction === 'desc' ? (
                                            <ArrowDown size={12} />
                                        ) : null}
                                    </button>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                    {displayRows.length > 0 ? (
                        displayRows.map((row, rowIndex) => {
                            const clickable = typeof onRowClick === 'function';
                            const extraRowClass =
                                typeof rowClassName === 'function' ? rowClassName(row, rowIndex) : rowClassName;
                            const key = rowKey(row, rowIndex);
                            const mainRow = (
                                <tr
                                    key={key}
                                    onClick={clickable ? () => onRowClick(row) : undefined}
                                    className={`transition-colors hover:bg-mono-50 dark:hover:bg-mono-950 ${clickable ? 'cursor-pointer' : ''} ${extraRowClass || ''}`.trim()}
                                >
                                    {columns.map((column) => {
                                        const align = column.align || 'left';
                                        const alignCellClass =
                                            align === 'right'
                                                ? 'text-right'
                                                : align === 'center'
                                                  ? 'text-center'
                                                  : 'text-left';
                                        const rawValue =
                                            typeof column.getValue === 'function'
                                                ? column.getValue(row)
                                                : row?.[column.key];

                                        return (
                                            <td
                                                key={column.key}
                                                className={`px-4 py-3 text-sm text-mono-700 dark:text-mono-300 ${alignCellClass} ${column.cellClassName || ''}`.trim()}
                                            >
                                                {typeof column.render === 'function'
                                                    ? column.render(row, rawValue)
                                                    : toSearchableString(rawValue) || '-'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );

                            // Saída idêntica à de antes quando a prop não veio:
                            // <tr> nu, sem Fragment envolvendo — nenhuma tela
                            // existente muda de árvore por causa da extensão.
                            if (typeof renderExpandedRow !== 'function') {
                                return mainRow;
                            }

                            // A key do Fragment é a MESMA da linha em qualquer
                            // estado: se ela alternasse com abrir/fechar, o
                            // React remontaria a linha inteira a cada clique.
                            const expandedContent = renderExpandedRow(row, rowIndex);
                            return (
                                <Fragment key={key}>
                                    {mainRow}
                                    {expandedContent ? (
                                        <tr
                                            key={`${key}-expanded`}
                                            // Sem hover nem cursor: o painel não é
                                            // uma linha clicável, é o corpo da que
                                            // está aberta logo acima.
                                            className="bg-mono-50 dark:bg-mono-900/40"
                                        >
                                            <td colSpan={colSpan} className="px-4 py-4 align-top">
                                                {/* min-w-0 + w-full: gráficos com
                                                    ResponsiveContainer medem o pai, e um
                                                    filho auto-dimensionado dentro de um
                                                    <td> alargaria a tabela em vez de
                                                    caber nela. */}
                                                <div className="w-full min-w-0">{expandedContent}</div>
                                            </td>
                                        </tr>
                                    ) : null}
                                </Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={colSpan}
                                className="px-4 py-8 text-center text-sm text-mono-500 dark:text-mono-400"
                            >
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </TableShell>
    );
};

export default StandardTable;
